import {
  AfterContentInit, AfterViewInit, Component, ContentChildren, ElementRef,
  EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, QueryList,
  SimpleChanges, ViewChild, ViewChildren, ViewEncapsulation
} from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { ChairTabComponent } from './chair-tab.component';

/**
 * Sistema de abas no padrão chair UI do JEMS3 — folder-tabs com encaixe
 * visual no painel, hover/selected sutis, e API estilo Material (declarativa
 * via `<app-chair-tab label="...">`).
 *
 * Características visuais (extraídas da página Pessoas):
 *   • Aba ativa "encaixa" no painel — sem `border-bottom`, mesmo bg do painel
 *   • Inativas com bg distinto, label peso 430, hover muito sutil
 *   • Painel com `border-radius: 0 8px 8px 8px` (top-left reto pra encaixar
 *     com a 1ª aba quando ela é a ativa)
 *   • Sombras (lateral na ativa, inset nas inativas) em opacidade baixa pra
 *     reforçar a hierarquia sem virar 3D pesado
 *
 * Comportamento:
 *   • `[(selectedIndex)]` two-way binding pro índice ativo
 *   • TODAS as abas ficam no DOM desde o load (inativas só ocultas) — preserva
 *     componentes/estado e o lifecycle de `<app-floating-submit-bar>` montadas
 *     desde o início. No `folder` as inativas são `display:none`; no
 *     `underline` ficam sobrepostas (absolute) pro carrossel (ver
 *     `panelTransform`).
 *   • Variante `underline`: troca de aba anima um carrossel horizontal — o
 *     conteúdo que sai desliza pra fora enquanto o novo entra pelo outro lado.
 *   • Acessibilidade: `role="tablist"`, `role="tab"`, `role="tabpanel"`,
 *     `aria-selected`, tabindex correto, suporte a setas (←→) pra navegar
 */
@Component({
  selector: 'app-chair-tabs',
  templateUrl: './chair-tabs.component.html',
  styleUrls: ['./chair-tabs.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChairTabsComponent
  implements AfterContentInit, AfterViewInit, OnChanges, OnDestroy {
  @ContentChildren(ChairTabComponent) tabs!: QueryList<ChairTabComponent>;

  /** Elementos <button> de cada aba — usados pra medir a ink bar (underline). */
  @ViewChildren('tabBtn') tabButtons!: QueryList<ElementRef<HTMLElement>>;

  /** Container rolável horizontalmente que envolve `.chair-tabs`. Quando o
   *  conteúdo é mais largo que ele, os chevrons aparecem. */
  @ViewChild('scroller') scrollerRef?: ElementRef<HTMLElement>;

  /** Estados de overflow horizontal — controlam a visibilidade dos chevrons. */
  canScrollLeft = false;
  canScrollRight = false;

  /** Observa o scroller pra recalcular `canScrollLeft/Right` quando a largura
   *  do container ou o conteúdo das abas mudar. */
  private stripResizeObserver?: ResizeObserver;

  /** Input de renomear (aparece no lugar do botão da aba em edição). */
  @ViewChildren('renameInput') renameInputs!: QueryList<ElementRef<HTMLInputElement>>;

  /** Painéis de conteúdo — usados pra medir a altura do ativo (underline). */
  @ViewChildren('panelEl') panelEls!: QueryList<ElementRef<HTMLElement>>;

  /** Altura medida do painel ativo (px); a viewport transiciona pra ela. */
  viewportHeight: number | null = null;

  /** Índice da aba ativa (0-based). Two-way via `[(selectedIndex)]`. */
  @Input() selectedIndex = 0;
  @Output() selectedIndexChange = new EventEmitter<number>();

  /**
   * Tratamento visual das abas:
   *   • `'folder'` (default) — abas que "encaixam" num painel/card contido.
   *     Para trocar VISÕES DE UM MESMO CARD (ex.: Pessoas).
   *   • `'underline'` — abas leves no nível do CANVAS, sem painel: linha de
   *     abas + rail inferior, indicador da ativa no token active. Conteúdo
   *     flui direto no canvas. Para trocar VISÕES INTEIRAS, onde o conteúdo
   *     já é uma grade de cards.
   *   • `'open-folder'` — mesma mecânica da `underline` (carrossel + ink bar +
   *     rail), MAS as abas ganham corpo de "pasta aberta": borda top/lados,
   *     cantos superiores arredondados, fundo cinza nas inativas e branco no
   *     hover; o bottom fica ABERTO sobre o rail (daí "open-folder"). Usada no
   *     Dashboard.
   *
   * `underline` e `open-folder` são "carrossel-like" (ver `isCarousel`):
   * compartilham ink bar deslizante, altura animada e slide horizontal.
   */
  @Input() appearance: 'folder' | 'underline' | 'open-folder' = 'folder';

  /** Rótulo opcional do tablist pra screen readers (substitui o default). */
  @Input() ariaLabel?: string;

  /**
   * Anima a troca de aba? Só faz diferença nas variantes carrossel
   * (`underline`/`open-folder`), onde a troca desliza os painéis e anima a
   * altura da viewport. Com `false` a troca é instantânea — a mecânica (JS)
   * continua idêntica, apenas as transições CSS são desligadas pela classe
   * `.chair-tabs-frame--no-transition`. Útil quando o conteúdo é um formulário
   * denso e o slide/reflow de altura pesa mais do que informa.
   */
  @Input() animated = true;

  /**
   * Modo de edição: quando true, renderiza uma aba-fantasma "+" no fim (emite
   * `addTab`) e um "x" no canto de cada aba (emite `closeTab` com o índice).
   * Afordâncias de edição declarativas — o consumidor decide o que criar/excluir.
   */
  @Input() editable = false;

  /** Pedido de criar nova aba (clique na aba-fantasma "+"). */
  @Output() addTab = new EventEmitter<void>();

  /** Pedido de fechar a aba `i` (clique no "x"). */
  @Output() closeTab = new EventEmitter<number>();

  /** Renomear aba `index` para `label` (duplo-clique no título, modo edição). */
  @Output() renameTab = new EventEmitter<{ index: number, label: string }>();

  /** Reordenar abas (drag pelo título, modo edição). O consumidor reordena a
   *  fonte de dados; aqui só emitimos os índices. */
  @Output() reorderTab = new EventEmitter<{ previousIndex: number, currentIndex: number }>();

  /** Índice da aba em renomeação inline (ou null). */
  editingIndex: number | null = null;

  /** Largura (px) da aba no momento da edição — o input herda esse tamanho pra
   *  não "pular"/crescer e a ink bar continuar alinhada. */
  renameWidth: number | null = null;

  /** aria-label/tooltip da aba-fantasma "+" (passado pelo consumidor, i18n). */
  @Input() addTabLabel?: string;

  /** aria-label/tooltip do "x" de fechar aba (passado pelo consumidor, i18n). */
  @Input() closeTabLabel?: string;

  /** Tooltip no título da aba quando em edição (ex.: "Duplo clique para editar"). */
  @Input() renameHintLabel?: string;

  /**
   * Geometria da ink bar (indicador deslizante da aba ativa — variante
   * underline). `left`/`width` acompanham o <button> ativo; a transição CSS faz
   * a barra VIAJAR continuamente entre abas em vez de sumir/reaparecer. Só vira
   * `is-ready` (com transição) após o primeiro posicionamento, pra não deslizar
   * de left:0 no load.
   */
  inkLeft = 0;
  inkWidth = 0;
  inkReady = false;

  /**
   * Modo `folder`: painéis inativos ficam `display:none`, mas historicamente
   * TODOS renderizavam desde o load (comentário no template). Em telas com
   * conteúdo pesado por painel (ex.: Pessoas — 4 tabelas × N linhas), isso
   * multiplica o custo de CD por 4.
   *
   * Estratégia lazy-keep-alive: um painel entra no DOM na PRIMEIRA vez que
   * é ativado e permanece daí em diante — troca de aba deixa de recriar
   * estado (form, seleção, filtros). O painel 0 é considerado montado
   * desde o load porque é a aba inicial.
   *
   * Só se aplica a `folder`. Variantes carrossel (`underline`, `open-folder`)
   * precisam de todos os painéis simultaneamente pra animar o slide.
   */
  private mountedIndices = new Set<number>([0]);

  isPanelMounted(i: number): boolean {
    if (this.isCarousel) { return true; }
    return this.mountedIndices.has(i);
  }

  private tabsSub?: Subscription;
  private btnSub?: Subscription;
  private panelSub?: Subscription;
  // Observa o painel ATIVO pra re-medir a altura quando o conteúdo muda (ex.:
  // gráficos carregam async, add/remove de gráfico) → a viewport acompanha.
  private resizeObserver?: ResizeObserver;
  private observedPanels: HTMLElement[] = [];

  ngAfterContentInit(): void {
    // Se o consumidor adicionar/remover tabs dinamicamente, garante que o
    // selectedIndex continue válido (clamp pro último).
    this.tabsSub = this.tabs.changes.subscribe(() => {
      if (this.selectedIndex >= this.tabs.length) {
        this.setIndex(Math.max(0, this.tabs.length - 1));
      }
    });
  }

  ngAfterViewInit(): void {
    // Reposiciona a ink bar quando o conjunto de botões muda (tabs add/remove/
    // reorder). DEFERIDO (scheduleInkUpdate) pra não alterar `inkLeft` no meio do
    // CD — senão dá ExpressionChangedAfterItHasBeenChecked (ex.: após drag-drop).
    this.btnSub = this.tabButtons.changes.subscribe(() => this.scheduleInkUpdate());
    // Re-observa o painel ativo e re-mede a altura quando o conjunto de painéis
    // muda (add/remove/reorder de abas).
    this.panelSub = this.panelEls.changes.subscribe(() => {
      this.observeActivePanel();
      this.scheduleHeightUpdate();
    });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleHeightUpdate());
    }
    // Observa o scroller pra atualizar o estado de overflow (chevrons) quando
    // o conjunto de abas, a largura do container OU a fonte mudar.
    const scrollerEl = this.scrollerRef?.nativeElement;
    if (scrollerEl && typeof ResizeObserver !== 'undefined') {
      this.stripResizeObserver = new ResizeObserver(() => this.updateScrollState());
      this.stripResizeObserver.observe(scrollerEl);
      // Observa também o conteúdo interno (.chair-tabs) — quando uma aba é
      // renomeada/adicionada, scrollWidth muda mas o tamanho do scroller não.
      const innerStrip = scrollerEl.querySelector('.chair-tabs') as HTMLElement | null;
      if (innerStrip) { this.stripResizeObserver.observe(innerStrip); }
    }
    // Primeiro posicionamento após o layout estabilizar; habilita a transição
    // num tick seguinte pra que o load não anime de left:0.
    setTimeout(() => {
      this.updateInk();
      this.observeActivePanel();
      this.updateHeight();
      this.updateScrollState();
      setTimeout(() => { this.inkReady = true; });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // selectedIndex/appearance vindos do pai (ex.: defaultDashboardIndex do
    // Dashboard) também movem a ink bar e a altura.
    if (changes.selectedIndex || changes.appearance) {
      this.mountedIndices.add(this.selectedIndex);
      this.scheduleInkUpdate();
      this.observeActivePanel();
      this.scheduleHeightUpdate();
    }
  }

  ngOnDestroy(): void {
    this.tabsSub?.unsubscribe();
    this.btnSub?.unsubscribe();
    this.panelSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.stripResizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Larguras/posições dos botões mudam com o resize → recalcula sem animar
    // (a transição é pra troca de aba, não pra reflow de layout).
    this.updateInk();
    this.updateHeight();
    this.updateScrollState();
  }

  /** Chamado pelo `(scroll)` no `.chair-tabs-scroller`. */
  onStripScroll(): void {
    this.updateScrollState();
  }

  /** Recalcula `canScrollLeft/Right` lendo geometria do scroller. */
  private updateScrollState(): void {
    const el = this.scrollerRef?.nativeElement;
    if (!el) { return; }
    const max = el.scrollWidth - el.clientWidth;
    // Tolerância de 1px pra absorver arredondamento (subpixel).
    this.canScrollLeft = el.scrollLeft > 1;
    this.canScrollRight = el.scrollLeft < max - 1;
  }

  /** Rola a faixa horizontalmente. `direction = -1` (esquerda) | `1` (direita).
   *  Quantidade = 70% da largura visível — sugere "mais conteúdo de onde veio". */
  scrollByDirection(direction: -1 | 1): void {
    const el = this.scrollerRef?.nativeElement;
    if (!el) { return; }
    const amount = el.clientWidth * 0.7 * direction;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  }

  /** Se a aba ativa está fora da área visível do scroller, rola pra ela. */
  private scrollActiveIntoView(): void {
    const scroller = this.scrollerRef?.nativeElement;
    const btn = this.tabButtons?.toArray()[this.selectedIndex]?.nativeElement;
    if (!scroller || !btn) { return; }
    const sLeft = scroller.scrollLeft;
    const sRight = sLeft + scroller.clientWidth;
    const bLeft = btn.offsetLeft;
    const bRight = bLeft + btn.offsetWidth;
    // Margem de "respiro" pra a aba ativa não ficar colada na borda.
    const margin = 12;
    if (bLeft < sLeft + margin) {
      scroller.scrollTo({ left: bLeft - margin, behavior: 'smooth' });
    } else if (bRight > sRight - margin) {
      scroller.scrollTo({ left: bRight - scroller.clientWidth + margin, behavior: 'smooth' });
    }
  }

  setIndex(i: number): void {
    if (i === this.selectedIndex) { return; }
    const tab = this.tabs?.toArray()[i];
    if (tab?.disabled) { return; }
    // Marca a transição pra que updateHeight use max(saindo, entrando)
    // temporariamente — evita clipping do slide de 420ms. Após esse
    // período, volta a usar só a altura do ativo.
    this.slidingFromIndex = this.selectedIndex;
    this.isSliding = true;
    this.selectedIndex = i;
    this.mountedIndices.add(i);
    this.selectedIndexChange.emit(i);
    this.scheduleInkUpdate();
    this.observeActivePanel();
    this.scheduleHeightUpdate();
    setTimeout(() => this.scrollActiveIntoView());
    setTimeout(() => { this.isSliding = false; this.scheduleHeightUpdate(); }, 440);
  }

  /** Drop do drag de reordenação — emite os índices pro consumidor reordenar. */
  onTabDrop(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) { return; }
    this.reorderTab.emit({
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
    });
  }

  /** Clique no "x" de uma aba — não seleciona a aba, só pede o fechamento. */
  onCloseTab(event: Event, i: number): void {
    event.stopPropagation();
    this.closeTab.emit(i);
  }

  /** Duplo-clique no título (modo edição) → renomeação inline da aba `i`. */
  startRename(i: number): void {
    if (!this.editable) { return; }
    // Captura a largura atual da aba ANTES de trocá-la pelo input, pra o input
    // ter exatamente o mesmo tamanho (sem crescer; ink bar segue alinhada).
    const btn = this.tabButtons?.toArray()[i]?.nativeElement;
    this.renameWidth = btn ? btn.offsetWidth : null;
    this.editingIndex = i;
    // Foca/seleciona o input no próximo tick (depois que ele entra no DOM).
    setTimeout(() => {
      const input = this.renameInputs?.first?.nativeElement;
      if (input) { input.focus(); input.select(); }
    });
  }

  /** Confirma a renomeação (Enter/blur). Ignora vazio e sem mudança. */
  commitRename(i: number, value: string): void {
    if (this.editingIndex !== i) { return; }   // evita duplo commit (Enter→blur)
    this.editingIndex = null;
    const label = (value || '').trim();
    const current = this.tabs?.toArray()[i];
    if (label && current && label !== current.label) {
      this.renameTab.emit({ index: i, label });
    }
  }

  /** Cancela a renomeação (Escape) sem emitir. */
  cancelRename(): void {
    this.editingIndex = null;
  }

  /** Mede o <button> ativo e posiciona a ink bar sobre ele (underline). */
  private updateInk(): void {
    if (!this.isCarousel) { return; }
    // Durante o rename, o botão da aba ativa é substituído pelo input → a
    // QueryList reindexa e a medição erraria. Mantém a barra parada até sair.
    if (this.editingIndex !== null) { return; }
    const el = this.tabButtons?.toArray()[this.selectedIndex]?.nativeElement;
    if (!el) { return; }
    this.inkLeft = el.offsetLeft;
    this.inkWidth = el.offsetWidth;
  }

  /** Atualiza a ink bar fora do ciclo atual de CD (evita ExpressionChanged). */
  private scheduleInkUpdate(): void {
    setTimeout(() => this.updateInk());
  }

  /** Mede a altura do painel ATIVO e aplica no viewport. Durante uma
   *  transição de aba (`isSliding=true`) usa `max(entrando, saindo)`
   *  temporariamente pra evitar clipping enquanto o slide roda; termina
   *  o slide e cai pra altura só do ativo — sem "vazio abaixo" quando o
   *  ativo é mais curto que a maior aba. ResizeObserver re-mede quando
   *  conteúdo async cresce. */
  private updateHeight(): void {
    if (!this.isCarousel) { this.viewportHeight = null; return; }
    const els = this.panelEls?.toArray() ?? [];
    if (!els.length) { return; }
    const activeEl = els[this.selectedIndex]?.nativeElement;
    const activeH = activeEl?.offsetHeight ?? 0;
    if (this.isSliding) {
      const prevEl = els[this.slidingFromIndex]?.nativeElement;
      const prevH = prevEl?.offsetHeight ?? 0;
      this.viewportHeight = Math.max(activeH, prevH);
    } else {
      this.viewportHeight = activeH;
    }
  }
  private isSliding = false;
  private slidingFromIndex = 0;

  /** Atualiza a altura fora do ciclo atual de CD (evita ExpressionChanged). */
  private scheduleHeightUpdate(): void {
    setTimeout(() => this.updateHeight());
  }

  /** Faz o ResizeObserver observar TODOS os painéis. Como `updateHeight` usa
   *  `max(painéis)`, qualquer painel pode definir a altura — não basta observar
   *  só o ativo. Re-chama a cada mudança de aba/QueryList pra cobrir adição/
   *  remoção dinâmica de tabs. */
  private observeActivePanel(): void {
    if (!this.isCarousel || !this.resizeObserver) { return; }
    const els = this.panelEls?.toArray().map(r => r?.nativeElement).filter(Boolean) ?? [];
    // Desobserva painéis que saíram (ex.: tab removida) e observa os novos.
    for (const old of this.observedPanels) {
      if (!els.includes(old)) { this.resizeObserver.unobserve(old); }
    }
    for (const el of els) {
      if (!this.observedPanels.includes(el)) { this.resizeObserver.observe(el); }
    }
    this.observedPanels = els;
  }

  /** Aba ativa — usado pelo template pra renderizar o conteúdo. */
  get activeTab(): ChairTabComponent | undefined {
    return this.tabs?.toArray()[this.selectedIndex];
  }

  /**
   * Variantes "carrossel-like" (`underline` e `open-folder`): compartilham a
   * MESMA mecânica — ink bar deslizante, altura animada da viewport e slide
   * horizontal dos painéis. A `folder` não (painéis empilhados via display:none).
   * Gate único usado por updateInk/updateHeight/observeActivePanel/panelTransform
   * e pelo template (ink bar + height).
   */
  get isCarousel(): boolean {
    return this.appearance === 'underline' || this.appearance === 'open-folder';
  }

  /**
   * Deslocamento horizontal do painel `i` no carrossel (variante `underline`).
   * Painéis ficam sobrepostos (absolute) e cada um é empurrado por
   * `(i - ativo) * 100%`: o ativo em 0, o vizinho à esquerda em -100%, o à
   * direita em +100%. Trocar de aba muda o `selectedIndex`, recalcula todos os
   * transforms e a transição CSS faz a viagem — o painel que sai desliza pra
   * fora por um lado enquanto o que entra chega pelo outro.
   *
   * Na `folder` retorna null: os painéis seguem empilhados via `display:none`
   * (sem carrossel), preservando o comportamento de Pessoas.
   */
  panelTransform(i: number): string | null {
    if (!this.isCarousel) { return null; }
    // Painel ATIVO em estado estável (sem slide em curso): NÃO retorna
    // transform. Qualquer valor não-none em `transform` cria containing
    // block pra `position: fixed` — o que faria descendentes fixos (ex.:
    // app-floating-submit-bar) ficarem ancorados no painel (clipado pelo
    // .chair-tab-panels), não na viewport. Durante o slide todos os
    // painéis ainda recebem transform pra animar.
    if (i === this.selectedIndex && !this.isSliding) { return null; }
    return `translateX(${(i - this.selectedIndex) * 100}%)`;
  }

  /** Teclado: setas movem o foco/seleção entre abas. */
  onKeydown(event: KeyboardEvent): void {
    const list = this.tabs?.toArray() ?? [];
    if (!list.length) { return; }
    let next: number | null = null;
    if (event.key === 'ArrowRight') { next = (this.selectedIndex + 1) % list.length; }
    else if (event.key === 'ArrowLeft') { next = (this.selectedIndex - 1 + list.length) % list.length; }
    else if (event.key === 'Home') { next = 0; }
    else if (event.key === 'End') { next = list.length - 1; }
    if (next !== null) {
      event.preventDefault();
      // Pula tabs disabled.
      let safety = list.length;
      while (list[next!].disabled && safety-- > 0) {
        next = event.key === 'ArrowLeft' || event.key === 'End'
          ? (next! - 1 + list.length) % list.length
          : (next! + 1) % list.length;
      }
      this.setIndex(next!);
    }
  }
}
